/**
 * The internal lane's middleware Tag. BROWSER-SAFE: the Tag and nothing else.
 *
 * Every BMS contract binds this with `.middleware(BmsSessionMiddleware)`, and
 * the browser imports the contract to build its client, so nothing here may
 * reach server-only code. The Live layer is `session-middleware-live.ts`, and
 * Tag and Live can never share a module on a lane whose contract is
 * browser-imported.
 *
 * A BMS-OWNED TAG rather than the shared `SessionAuthMiddleware`, for the
 * reason `calls-session-middleware.ts` gives: the shared one fails with FLAT
 * tagged errors, and the boundary turns any tag it does not recognise as an
 * envelope error into an opaque 500, so an expired session would reach a
 * broker as "something broke" instead of "sign in again". What is shared is
 * the RESOLUTION (`makeResolveSessionPrincipal`), which is where the risk
 * lives.
 *
 * NO FEATURE FLAG GATE. The calls lane carries one because it launched behind
 * `calls_user_features`; the BMS book does not, so there is no flag lookup and
 * no fail-closed 404 arm.
 *
 * THE FAILURE UNION IS THREE MEMBERS: 401 when there is no verified session,
 * 503 when identity resolution is down, and 403 when the caller named a
 * platform scope this organisation does not hold. The third arrived with the
 * scope work: the shared resolution admits the request's scope, and a refusal
 * there is a verdict about a product and not about signing in. Dressed as the
 * 401 it would otherwise fall back to, it offered a broker a sign-in for a
 * problem signing in again cannot fix. `ApiInsufficientScopeWire` is the
 * catalogue's 403 and `withBmsErrors` already declares it on every group, so
 * the refusal rides an envelope the lane publishes rather than a new class.
 */
import { HttpApiMiddleware } from "@effect/platform"
import { Schema as S } from "effect"

import { BmsPrincipalTag } from "../domain/principal"
// RELATIVE, not the public-api barrel. See the header.
import {
  ApiEntitlementRequiredWire,
  ApiInsufficientScopeWire,
  ApiServiceUnavailableWire,
  ApiUnauthorizedWire,
} from "../../../public-api/domain/public-api-error"

export class BmsSessionMiddleware extends HttpApiMiddleware.Tag<BmsSessionMiddleware>()(
  "Bms/BmsSessionMiddleware",
  {
    provides: BmsPrincipalTag,
    // The fourth, decision 033: a firm whose Cova CRM switches leave the
    // route off is the catalogue's 403 `entitlement_required`, one message
    // that names no switch, before any handler or statement runs.
    failure: S.Union(ApiUnauthorizedWire, ApiInsufficientScopeWire, ApiServiceUnavailableWire, ApiEntitlementRequiredWire),
  },
) {}
